import { ChangeDetectionStrategy, Component, Input } from "@angular/core";
import { EspIconSize, lyraIconHref } from "./esp-icon";

/**
 * Lyra (Lucide) icon from ESP's bundled sprite (`assets/icons/lyra-sprite.svg`,
 * injected into <body> by IconSpriteLoaderService).
 *
 *   <esp-icon name="x"></esp-icon>                      decorative, 16px
 *   <esp-icon name="help" [size]="20"></esp-icon>       toolbar icon-button size
 *   <esp-icon name="warning" label="Warning"></esp-icon> meaningful: role="img"
 *
 * `name` is the Lyra name without the `lyra-` prefix (see src/assets/icons/icons.json).
 * The icon is drawn in currentColor. Icon-only buttons keep their own aria-label;
 * the icon inside stays decorative.
 */
@Component({
  selector: "esp-icon",
  template: `<svg class="esp-icon"
                  [attr.role]="label ? 'img' : null"
                  [attr.aria-label]="label || null"
                  [attr.aria-hidden]="label ? null : 'true'"
                  focusable="false"><use [attr.href]="href"></use></svg>`,
  host: {
    "class": "esp-icon-host",
    "[class.esp-icon-xs]": "size === 12",
    "[class.esp-icon-md]": "size === 20",
    "[class.esp-icon-lg]": "size === 24",
    "[attr.data-icon]": "name"
  },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EspIconComponent {
  /** Lyra icon name without the `lyra-` prefix, e.g. "x", "help", "chevron-down". */
  @Input({ required: true }) name!: string;
  /** Pixel size: 16 (default, --lyra-icon-size-sm), 20 (md), 24 (lg) or 12 (xs). */
  @Input() size: EspIconSize = 16;
  /** Accessible name. Omit for decorative icons (rendered aria-hidden). */
  @Input() label?: string | null;

  get href(): string {
    return lyraIconHref(this.name);
  }
}
